import React, { ReactNode } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TextArea } from '../../common/TextArea'
import { Button, ButtonVariant } from '../../common/Button'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { commentSchema } from '../../../forms/schemas/comment'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'
import { patchComment } from '../../../api/comments'
import { CommentRow } from '../../../generated/api/comment-row'
import { refreshComment, updateComment } from '../commentCache'

interface CommentEditProps {
  comment: CommentRow
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>
}

export const CommentEdit = ({
  comment,
  setEditMode,
}: CommentEditProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<InferType<typeof commentSchema>>({
    mode: 'all',
    resolver: yupResolver(commentSchema),
    defaultValues: { comment: comment.message },
  })
  const queryClient = useQueryClient()

  const { mutate: mutatePatchComment, isPending } = useMutation({
    mutationFn: patchComment,
    meta: { skipGlobalInvalidate: true },
    onSuccess: (_data, { message }) => {
      setEditMode(false)
      updateComment(queryClient, comment.postId, comment.id, (row) => ({
        ...row,
        message,
      }))
      return refreshComment(queryClient, comment.postId, comment.id).catch(
        () => undefined
      )
    },
    onError: () => {
      showErrorMessage()
    },
  })

  const onSubmit = async (
    data: InferType<typeof commentSchema>
  ): Promise<void> => {
    mutatePatchComment({ id: comment.id, message: data.comment })
  }

  const onError = (): void => {
    showErrorMessage()
  }

  const onClose = (): void => {
    setEditMode(false)
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Controller
        control={control}
        name="comment"
        render={({ field: { value, onChange, onBlur } }) => (
          <TextArea
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            fixedHeight={false}
          />
        )}
      />
      <div className="flex flex-wrap justify-end gap-4">
        <Button
          label={translate('common.actions.cancel')}
          onClick={onClose}
          size="sm"
        />
        <Button
          variant={ButtonVariant.PRIMARY}
          disabled={!isValid || isPending}
          label={translate('common.actions.save')}
          type="submit"
          size="sm"
        />
      </div>
    </form>
  )
}
